import { useState } from 'react';
import SEO from '../components/SEO';

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('General');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch('/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // The Worker currently expects { email }. We include extra fields for future use.
        body: JSON.stringify({ email, name, topic, message, type: 'contact' })
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) {
        setName('');
        setEmail('');
        setTopic('General');
        setMessage('');
      }
    } catch (_) {
      setStatus('error');
    }
  };

  return (
    <div className="flex-1 bg-white py-12">
      <SEO
        title="Contact Us | Waybook"
        description="Have a question or feedback? Get in touch with the Waybook team. We're here to help with your travel planning needs."
        keywords="contact, support, feedback, customer service, help"
        canonicalUrl="https://www.waybook.me/contact"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Us',
          description: 'Contact Waybook support team',
          url: 'https://www.waybook.me/contact'
        }}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8">Contact Us</h1>

        <p className="text-gray-700 mb-8">
          Have a question, feedback, or partnership idea? Send us a message.
          We’ll get back to you via email. You can also reach us at
          <a className="ml-1 underline" href="mailto:support@waybook.me">support@waybook.me</a>.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="Your name"
                required
                disabled={status === 'loading'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                placeholder="you@example.com"
                required
                disabled={status === 'loading'}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
            <select
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              disabled={status === 'loading'}
            >
              <option>General</option>
              <option>Partnership</option>
              <option>Advertising</option>
              <option>Influencers</option>
              <option>Business</option>
              <option>Press</option>
              <option>Support</option>
              <option>Developers</option>
              <option>Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full min-h-[140px] p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              placeholder="How can we help?"
              required
              disabled={status === 'loading'}
            />
          </div>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={status === 'loading'}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-900 disabled:opacity-60"
            >
              {status === 'loading' ? 'Sending…' : 'Send Message'}
            </button>
            {status === 'success' && (
              <span className="text-green-600">Thanks! We'll reply to your message.</span>
            )}
            {status === 'error' && (
              <span className="text-red-600">Something went wrong. Try again.</span>
            )}
          </div>
        </form>

        <div className="mt-10 p-6 bg-gray-50 border border-gray-200 rounded-lg">
          <h2 className="text-xl font-semibold mb-3">Direct Contacts</h2>
          <ul className="text-gray-700 space-y-1">
            <li>Support: <a className="underline" href="mailto:support@waybook.me">support@waybook.me</a></li>
            <li>Advertising: <a className="underline" href="mailto:advertising@waybook.me">advertising@waybook.me</a></li>
            <li>Influencers: <a className="underline" href="mailto:influencers@waybook.me">influencers@waybook.me</a></li>
            <li>Business: <a className="underline" href="mailto:business@waybook.me">business@waybook.me</a></li>
            <li>Press: <a className="underline" href="mailto:press@waybook.me">press@waybook.me</a></li>
            <li>Developers: <a className="underline" href="mailto:developers@waybook.me">developers@waybook.me</a></li>
          </ul>
        </div>
      </div>
    </div>
  );
}

